import React, { useEffect, useMemo, useState } from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Card from '@/components/layout/Card';
import AlertBox from '@/components/layout/AlertBox';
import Botao from '@/components/Botao';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import Icon from '@mdi/react';
import { mdiLoading, mdiPlus, mdiTrashCan } from '@mdi/js';
import { useAppliance } from '@/lib/useAppliance';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { toastError, toastSuccess } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import type {
    QosConfigDTO,
    QosContextoDTO,
    QosDispositivoDTO,
    QosLanDTO,
    QosPrioridadeDispositivoDTO,
    QosRespostaDTO,
    QosWanDTO,
} from '@webip/shared/src/dto/qos';
import styles from './QosPage.module.css';
import Grid, { GridItem } from '@/components/layout/Grid';

/** Fração do link que a raiz da fila usa (espelha qosLinkPct no adapter-mk). */
const LINK_PCT = 0.95;

const prioridadeOptions: { id: QosPrioridadeDispositivoDTO; label: string }[] = [
    { id: 'alta', label: 'Alta prioridade' },
    { id: 'voz', label: 'Voz (telefone / PABX)' },
    { id: 'normal', label: 'Normal (só mínimo/teto)' },
];

/** Campo numérico vazio → null; CampoNumero entrega o valor cru como string. */
function num(v: unknown): number | null {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

// '' (e não undefined) para o campo esvaziar de fato — ver CampoNumero.
const kbpsParaMbps = (kbps: number | null | undefined): number | '' => (kbps == null ? '' : kbps / 1000);
const mbpsParaKbps = (v: unknown) => {
    const n = num(v);
    return n == null || n <= 0 ? null : Math.round(n * 1000);
};

/** "47,5" — Mbps com no máximo uma casa, no formato da tela. */
function fmtMbps(kbps: number): string {
    const mbps = kbps / 1000;
    return mbps.toLocaleString('pt-BR', { maximumFractionDigits: mbps < 10 ? 1 : 0 });
}

function lanPadrao(lanId: number): QosLanDTO {
    return { lan_id: lanId, garantia_pct: null, teto_pct: null, despriorizar_pesado: true };
}

type LinkControlado = { id: number; nome: string; down: number; up: number };

/** Cor de cada rede na barra (voz é sempre âmbar). */
const CORES_LAN = [
    'var(--tc-color-primary)',
    'var(--tc-color-info-600)',
    'var(--tc-color-success-600)',
    'var(--tc-color-danger-500)',
    'var(--tc-color-primary-400)',
    'var(--tc-color-info-400)',
];

/**
 * Resumo enxuto por link: garantias empilhadas em sólido (voz + redes) e o
 * teto de cada rede como marca tracejada — teto é opcional e não reserva
 * nada, só limita. Percentuais são iguais em todo link; muda o Mbps.
 */
function BarrasDistribuicao({ links, model, contexto }: {
    links: LinkControlado[];
    model: QosConfigDTO;
    contexto: QosContextoDTO;
}) {
    if (links.length === 0) {
        return <span className={styles.muted}>Informe a velocidade de um link para ver a distribuição.</span>;
    }
    const voz = model.voip.ativo && contexto.agent_type !== 'opnsense' ? model.voip.reserva_pct : 0;
    const lans = contexto.lans.map((l, i) => {
        const c = model.lans.find((x) => x.lan_id === l.id);
        // No OPNsense não há teto (ver facility_qos.go do adapter-opnsense): sem marca na barra.
        const teto = contexto.agent_type === 'opnsense' ? 0 : c?.teto_pct ?? 0;
        return { id: l.id, nome: l.nome, cor: CORES_LAN[i % CORES_LAN.length], garantia: c?.garantia_pct ?? 0, teto };
    });
    const temTeto = lans.some((l) => l.teto > 0);

    return (
        <div className={styles.distrib}>
            {links.map((link) => (
                <div key={link.id} className={styles.distribLinha}>
                    <span className={styles.distribNome} title={`${link.nome}: ${fmtMbps(link.down)}↓ / ${fmtMbps(link.up)}↑ Mbps controlados`}>
                        {link.nome}
                    </span>
                    <div className={styles.miniBar}>
                        {voz > 0 && (
                            <div className={styles.miniSeg} style={{ width: `${Math.min(100, voz)}%`, background: 'var(--tc-color-warning-750)' }}
                                title={`Chamadas: até ${voz}% ≈ ${fmtMbps((link.down * voz) / 100)} Mbps`} />
                        )}
                        {lans.filter((l) => l.garantia > 0).map((l) => (
                            <div key={l.id} className={styles.miniSeg} style={{ width: `${Math.min(100, l.garantia)}%`, background: l.cor }}
                                title={`${l.nome}: garantia ${l.garantia}% ≈ ${fmtMbps((link.down * l.garantia) / 100)} Mbps`} />
                        ))}
                        {lans.filter((l) => l.teto > 0).map((l) => (
                            <div key={`teto-${l.id}`} className={styles.miniTeto} style={{ left: `${Math.min(100, l.teto)}%`, borderColor: l.cor }}
                                title={`${l.nome}: teto ${l.teto}% ≈ ${fmtMbps((link.down * l.teto) / 100)} Mbps (opcional)`} />
                        ))}
                    </div>
                </div>
            ))}
            <div className={styles.miniLegenda}>
                {voz > 0 && <span><i className={styles.chip} style={{ background: 'var(--tc-color-warning-750)' }} />voz</span>}
                {lans.filter((l) => l.garantia > 0 || l.teto > 0).map((l) => (
                    <span key={l.id}><i className={styles.chip} style={{ background: l.cor }} />{l.nome}</span>
                ))}
                {temTeto && <span><i className={styles.chipTeto} />teto</span>}
            </div>
        </div>
    );
}

export default function QosPage() {
    const { applianceId, selectedAppliance } = useAppliance();
    const qc = useQueryClient();
    const [model, setModel] = useState<QosConfigDTO | null>(null);
    const [contexto, setContexto] = useState<QosContextoDTO | null>(null);

    const query = useQuery(
        ['qos-config', applianceId],
        async () => {
            const res = await axios.get<{ ok: boolean; result: QosRespostaDTO }>(
                `/api/appliance/${applianceId}/qos`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        {
            enabled: !!applianceId,
            retry: false,
            // Refetch ao voltar o foco sobrescreveria edições ainda não salvas.
            refetchOnWindowFocus: false,
            onSuccess: (data) => {
                setModel(data.config);
                setContexto(data.contexto);
            },
        },
    );

    useEffect(() => {
        setModel(null);
        setContexto(null);
    }, [applianceId]);

    const mutSave = useMutation({
        mutationFn: async (payload: QosConfigDTO) =>
            axios.put<{ ok: boolean; result: QosRespostaDTO }>(`/api/appliance/${applianceId}/qos`, payload, { withCredentials: true }),
        onSuccess: (res) => {
            setModel(res.data.result.config);
            setContexto(res.data.result.contexto);
            toastSuccess('Controle de banda salvo! Sincronize a appliance para aplicar.');
            qc.setQueryData(['qos-config', applianceId], res.data.result);
            qc.setQueryData(['qos-config-sidebar', applianceId], res.data.result);
        },
        onError: (error: unknown) => toastError(extractApiError(error, 'Erro ao salvar').message),
    });

    // ── Derivados ──────────────────────────────────────────────────────────
    const links: LinkControlado[] = useMemo(() => {
        if (!model || !contexto) return [];
        return contexto.wans.flatMap((w) => {
            const c = model.wans.find((x) => x.wan_id === w.id);
            if (!c?.download_kbps || !c?.upload_kbps) return [];
            return [{ id: w.id, nome: w.nome, down: c.download_kbps * LINK_PCT, up: c.upload_kbps * LINK_PCT }];
        });
    }, [model, contexto]);

    if (!applianceId) {
        return (
            <LayoutUsuario>
                <VBox style={{ gap: '16px', padding: '16px' }}>
                    <PageHeader title="Controle de banda" />
                    <p style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>Selecione um appliance no menu lateral.</p>
                </VBox>
            </LayoutUsuario>
        );
    }

    const carregando = query.isLoading || !model || !contexto;
    // OPNsense (dummynet) não tem árvore de filas nem prioridade estrita:
    // garantia vira peso, teto da rede vira pipe próprio, sem "downloads longos".
    const opnsense = selectedAppliance?.agent_type === 'opnsense';

    // ── Atualizadores ──────────────────────────────────────────────────────
    const set = (patch: Partial<QosConfigDTO>) => setModel((m) => (m ? { ...m, ...patch } : m));
    const setVoip = (patch: Partial<QosConfigDTO['voip']>) =>
        setModel((m) => (m ? { ...m, voip: { ...m.voip, ...patch } } : m));

    const wanCfg = (id: number): QosWanDTO =>
        model?.wans.find((w) => w.wan_id === id) ?? { wan_id: id, download_kbps: null, upload_kbps: null };
    const setWan = (id: number, patch: Partial<QosWanDTO>) =>
        setModel((m) => {
            if (!m) return m;
            const atual = m.wans.find((w) => w.wan_id === id) ?? { wan_id: id, download_kbps: null, upload_kbps: null };
            return { ...m, wans: [...m.wans.filter((w) => w.wan_id !== id), { ...atual, ...patch }] };
        });

    const lanCfg = (id: number): QosLanDTO => model?.lans.find((l) => l.lan_id === id) ?? lanPadrao(id);
    const setLan = (id: number, patch: Partial<QosLanDTO>) =>
        setModel((m) => {
            if (!m) return m;
            const atual = m.lans.find((l) => l.lan_id === id) ?? lanPadrao(id);
            return { ...m, lans: [...m.lans.filter((l) => l.lan_id !== id), { ...atual, ...patch }] };
        });

    const setDev = (idx: number, patch: Partial<QosDispositivoDTO>) =>
        setModel((m) => (m ? { ...m, dispositivos: m.dispositivos.map((d, i) => (i === idx ? { ...d, ...patch } : d)) } : m));
    const removeDev = (idx: number) =>
        setModel((m) => (m ? { ...m, dispositivos: m.dispositivos.filter((_, i) => i !== idx) } : m));
    const addDev = () =>
        setModel((m) => {
            if (!m || !contexto) return m;
            const usados = new Set(m.dispositivos.map((d) => d.dispositivo_id));
            const livre = contexto.dispositivos.find((d) => d.ip && !usados.has(d.id));
            if (!livre) {
                toastError('Todos os dispositivos com IP fixo já estão na lista.');
                return m;
            }
            return {
                ...m,
                dispositivos: [...m.dispositivos, {
                    dispositivo_id: livre.id, prioridade: 'alta',
                    garantia_download_kbps: null, garantia_upload_kbps: null, teto_download_kbps: null, teto_upload_kbps: null,
                }],
            };
        });

    /** "≈ Vivo 237/47 · Claro 47/23 Mbps" para um percentual de cada link. */
    const estimativa = (pct: number | null) => {
        if (!pct || links.length === 0) return null;
        return '≈ ' + links
            .map((l) => `${l.nome} ${fmtMbps((l.down * pct) / 100)}↓/${fmtMbps((l.up * pct) / 100)}↑`)
            .join(' · ') + ' Mbps';
    };

    // Espelha limiteGarantias do backend (services/qos.ts): no MikroTik 5% de
    // cada link ficam para a fila de ACKs TCP.
    const limiteGarantias = contexto?.agent_type === 'opnsense' ? 100 : 95;
    const somaGarantias = model
        ? model.lans.filter((l) => contexto?.lans.some((x) => x.id === l.lan_id)).reduce((s, l) => s + (l.garantia_pct ?? 0), 0)
        // No OPNsense a voz é só peso, sem reserva — não entra na soma.
        + (model.voip.ativo && contexto?.agent_type !== 'opnsense' ? model.voip.reserva_pct : 0)
        : 0;

    return (
        <LayoutUsuario>
            <VBox style={{ gap: '16px', padding: '16px' }}>
                <PageHeader
                    title={`Controle de banda${selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}`}
                    description="Divide a banda de cada link entre as redes, prioriza chamadas e dispositivos e reduz a prioridade de downloads longos."
                />

                {!carregando && model && contexto && (
                    // Fica no topo ao rolar: o resumo da distribuição acompanha a edição.
                    <div className={styles.stickyBar}>
                        <div className={styles.stickyResumo}>
                            <BarrasDistribuicao links={links} model={model} contexto={contexto} />
                        </div>
                        {/* div e não HBox: o HBox tem width 100% e espremeria o resumo. */}
                        <div className={styles.stickyAcoes}>
                            <CampoCheckbox text="Ativo" value={model.ativo} disabled={links.length === 0}
                                onChange={(v) => set({ ativo: v })} />
                            <Botao variant="primary" onClick={() => mutSave.mutate(model)} disabled={mutSave.isLoading}>
                                {mutSave.isLoading ? 'Salvando...' : 'Salvar'}
                            </Botao>
                        </div>
                    </div>
                )}

                {carregando && (
                    <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                        <Icon path={mdiLoading} size={1.5} spin />
                    </HBox>
                )}

                {!carregando && model && contexto && (
                    <div className={styles.sections}>
                        {links.length === 0 && (
                            <AlertBox variant="warning">
                                Informe a velocidade contratada de pelo menos um link para ativar o controle de banda.
                                Sem ela o router não consegue ser o gargalo do link, e prioridade e divisão não têm efeito.
                            </AlertBox>
                        )}
                        {opnsense && links.length > 0 && (
                            <AlertBox variant="info">
                                <p>No OPNsense algumas opções são aproximadas: garantias viram <b>proporção</b> na disputa (não mínimo fixo),
                                    e chamadas e alta prioridade ganham <b>peso maior</b> com fila de baixa latência (não passam na frente de tudo).
                                    <b>Tetos</b>, reserva para chamadas e downloads longos com menor prioridade não estão disponíveis:
                                    no OPNsense um limite próprio fica fora do controle do link e, no horário de pico, anularia a prioridade das chamadas.</p>
                            </AlertBox>
                        )}
                        {somaGarantias > limiteGarantias && (
                            <AlertBox variant="danger">
                                A soma das garantias das redes{model.voip.ativo && !opnsense ? ' com a reserva para chamadas' : ''} passa
                                de {limiteGarantias}% ({somaGarantias}%){!opnsense && ' — 5% de cada link ficam reservados para os ACKs TCP'}.
                            </AlertBox>
                        )}

                        {/* ── Links ─────────────────────────────────────── */}
                        <Card>
                            <Card.Header title="Links" />
                            <Card.Body>
                                <div className={styles.muted} style={{ marginBottom: 8 }}>
                                    Velocidade contratada de cada link. Cada link é controlado separadamente, com 95% da velocidade
                                    informada — link sem velocidade fica sem controle.
                                </div>
                                {contexto.wans.length === 0 && <div className={styles.muted}>Nenhuma WAN cadastrada.</div>}
                                {contexto.wans.map((w) => {
                                    const c = wanCfg(w.id);
                                    const ok = !!c.download_kbps && !!c.upload_kbps;
                                    return (
                                        <div key={w.id} className={styles.row}>
                                            <div className={styles.name}>
                                                <div>
                                                    {w.nome}{' '}
                                                    <span className={styles.muted}>({w.uso === 'backup' ? 'backup' : 'primário'})</span>
                                                </div>
                                                <div className={styles.status}>
                                                    <span className={[styles.dot, ok ? styles.dotOn : ''].join(' ')} />
                                                    {ok ? 'controlado' : 'sem controle'}
                                                </div>
                                            </div>
                                            <div className={styles.field}>
                                                <CampoNumero
                                                    label="Download (Mbps)"
                                                    value={kbpsParaMbps(c.download_kbps)}
                                                    decimais={1}
                                                    somentePositivos
                                                    onChange={(v) => setWan(w.id, { download_kbps: mbpsParaKbps(v) })}
                                                />
                                            </div>
                                            <div className={styles.field}>
                                                <CampoNumero
                                                    label="Upload (Mbps)"
                                                    value={kbpsParaMbps(c.upload_kbps)}
                                                    decimais={1}
                                                    somentePositivos
                                                    onChange={(v) => setWan(w.id, { upload_kbps: mbpsParaKbps(v) })}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </Card.Body>
                        </Card>

                        {/* Sem velocidade nenhuma não há o que configurar além dos links. */}
                        {links.length > 0 && (<>
                            {/* ── Chamadas ──────────────────────────────────── */}
                            <Card>
                                <Card.Header title="Chamadas (VoIP)">
                                    <CampoCheckbox text="Priorizar" value={model.voip.ativo} onChange={(v) => setVoip({ ativo: v })} />
                                </Card.Header>
                                {model.voip.ativo && (
                                    <Card.Body>
                                        <VBox style={{ gap: 8 }}>
                                            <div className={styles.muted}>
                                                Chamadas passam na frente de todo o resto e não contam no teto das redes. Dispositivos marcados como
                                                "Voz" abaixo (telefones IP, PABX) entram sempre nesta classe.
                                            </div>
                                            <div className={styles.row}>
                                                <div className={styles.check}>
                                                    <CampoCheckbox text="SIP / RTP (PABX, softphone)" value={model.voip.sip} onChange={(v) => setVoip({ sip: v })} />
                                                </div>
                                                {model.voip.sip && (
                                                    <>
                                                        <div className={styles.field}>
                                                            <CampoNumero label="Portas RTP de" value={model.voip.rtp_inicio} somentePositivos
                                                                onChange={(v) => setVoip({ rtp_inicio: num(v) ?? 0 })} />
                                                        </div>
                                                        <div className={styles.field}>
                                                            <CampoNumero label="até" value={model.voip.rtp_fim} somentePositivos
                                                                onChange={(v) => setVoip({ rtp_fim: num(v) ?? 0 })} />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <div className={styles.row}>
                                                <div className={styles.check}>
                                                    <CampoCheckbox text="Teams, Zoom e Meet" value={model.voip.conferencia}
                                                        hint="Inclui vídeo e tela compartilhada. Se a rede bloquear UDP, eles usam a porta 443 e não são reconhecidos."
                                                        onChange={(v) => setVoip({ conferencia: v })} />
                                                </div>
                                            </div>
                                            <div className={styles.row}>
                                                <div className={styles.check}>
                                                    <CampoCheckbox text="Pacotes marcados como voz (DSCP EF)" value={model.voip.dscp}
                                                        onChange={(v) => setVoip({ dscp: v })} />
                                                </div>
                                            </div>
                                            {/* No OPNsense a voz é só peso alto (sem reserva nem teto). */}
                                            {!opnsense && (
                                                <div className={styles.row}>
                                                    <div className={styles.field}>
                                                        <CampoNumero label="Reservar até (%)" value={model.voip.reserva_pct} somentePositivos
                                                            onChange={(v) => setVoip({ reserva_pct: num(v) ?? 0 })} />
                                                    </div>
                                                    <div className={styles.estimate}>
                                                        {estimativa(model.voip.reserva_pct)}
                                                        {' '}— só é ocupada quando há chamada; também é o máximo da classe.
                                                    </div>
                                                </div>
                                            )}
                                        </VBox>
                                    </Card.Body>
                                )}
                            </Card>

                            {/* ── Redes ─────────────────────────────────────── */}
                            <Card>
                                <Card.Header title="Redes">
                                    <CampoCheckbox text="Dividir igualmente entre quem está usando" value={model.divisao_justa}
                                        onChange={(v) => set({ divisao_justa: v })} />
                                </Card.Header>
                                <Card.Body>
                                    <div className={styles.muted} style={{ marginBottom: 8 }}>
                                        Percentuais valem para cada link. Mínimo é o mínimo quando há disputa (sem disputa a rede usa mais);
                                        teto é o máximo absoluto, mesmo com o link ocioso. Vazio = sem limite.
                                    </div>
                                    {contexto.lans.length === 0 && <div className={styles.muted}>Nenhuma LAN cadastrada.</div>}
                                    {contexto.lans.map((l) => {
                                        const c = lanCfg(l.id);
                                        return (
                                            <div key={l.id} className={[styles.row, styles.lanRow].join(' ')}>
                                                <div className={styles.name}>{l.nome}</div>
                                                {/* Campos, checkbox e estimativa numa coluna à direita,
                                                    alinhados pelo início dos campos. */}
                                                <div className={styles.lanBody}>
                                                    <div className={styles.lanFields}>
                                                        <div className={styles.field}>
                                                            <CampoNumero label="Garantia (%)" value={c.garantia_pct ?? ''} somentePositivos
                                                                placeholder='(Desligado)' hint={opnsense ? 'Proporção na disputa' : undefined}
                                                                onChange={(v) => setLan(l.id, { garantia_pct: num(v) })} />
                                                        </div>
                                                        <div className={styles.field}>
                                                            <CampoNumero label="Teto (%)" value={opnsense ? '' : c.teto_pct ?? ''} somentePositivos
                                                                placeholder={opnsense ? '(Indisponível)' : '(Desligado)'} disabled={opnsense}
                                                                hint={opnsense ? 'Não disponível no OPNsense' : undefined}
                                                                onChange={(v) => setLan(l.id, { teto_pct: num(v) })} />
                                                        </div>
                                                    </div>
                                                    {!opnsense && (
                                                        <div className={styles.check}>
                                                            <CampoCheckbox text="Downloads longos com menor prioridade" value={c.despriorizar_pesado}
                                                                onChange={(v) => setLan(l.id, { despriorizar_pesado: v })} />
                                                        </div>
                                                    )}
                                                    {(() => {
                                                        const teto = opnsense ? null : c.teto_pct;
                                                        if (!(c.garantia_pct || teto) || links.length === 0) return null;
                                                        return (
                                                            <div className={styles.estimate}>
                                                                {c.garantia_pct ? `Garantia ${estimativa(c.garantia_pct)}` : ''}
                                                                {c.garantia_pct && teto ? ' — ' : ''}
                                                                {teto ? `Teto ${estimativa(teto)}` : ''}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Card.Body>
                            </Card>

                            {/* ── Dispositivos ──────────────────────────────── */}
                            <Card>
                                <Card.Header title="Dispositivos com tratamento especial">
                                    <Botao size="sm" variant="outline-primary" icon={mdiPlus} onClick={addDev}>Adicionar</Botao>
                                </Card.Header>
                                <Card.Body>
                                    <div className={styles.muted} style={{ marginBottom: 8 }}>
                                        Garantia e teto valem em cada link, por sentido (↓ download, ↑ upload). Só dispositivos com IP fixo.
                                    </div>
                                    {model.dispositivos.length === 0 && <div className={styles.muted}>Nenhum dispositivo — todos entram no rateio da rede.</div>}
                                    {model.dispositivos.map((d, idx) => {
                                        const usados = new Set(model.dispositivos.filter((_, i) => i !== idx).map((x) => x.dispositivo_id));
                                        const opcoes = contexto.dispositivos
                                            .filter((x) => x.ip && !usados.has(x.id))
                                            .map((x) => ({ id: String(x.id), label: `${x.nome} (${x.ip})` }));
                                        const voz = d.prioridade === 'voz';
                                        return (
                                            <HBox key={idx}>
                                                <Grid>
                                                    <GridItem sm={12} md={6}>
                                                        <CampoSeletor label="Dispositivo" value={String(d.dispositivo_id)} options={opcoes}
                                                            onChange={(v) => v && setDev(idx, { dispositivo_id: Number(v) })} />
                                                    </GridItem>
                                                    <GridItem sm={12} md={6}>
                                                        <CampoSeletor label="Tratamento" value={d.prioridade} options={prioridadeOptions} noSearch
                                                            onChange={(v) => v && setDev(idx, { prioridade: v as QosPrioridadeDispositivoDTO })} />
                                                    </GridItem>
                                                    {([
                                                        ['garantia_download_kbps', 'Mínimo ↓ (Mbps)'],
                                                        ['garantia_upload_kbps', 'Mínimo ↑ (Mbps)'],
                                                        ['teto_download_kbps', 'Teto ↓ (Mbps)'],
                                                        ['teto_upload_kbps', 'Teto ↑ (Mbps)'],
                                                    ] as const).map(([campo, label]) => (
                                                        <GridItem sm={12} md={6} lg={3} key={campo} className={styles.field}>
                                                            <CampoNumero label={label} value={voz || (opnsense && campo.startsWith('teto')) ? '' : kbpsParaMbps(d[campo])} decimais={1}
                                                                placeholder={opnsense && campo.startsWith('teto') ? '(Indisponível)' : '(Desligado)'} somentePositivos
                                                                disabled={voz || (opnsense && campo.startsWith('teto'))}
                                                                hint={opnsense ? (campo.startsWith('garantia') ? 'Proporção na disputa' : 'Não disponível no OPNsense') : undefined}
                                                                onChange={(v) => setDev(idx, { [campo]: mbpsParaKbps(v) })} />
                                                        </GridItem>
                                                    ))}
                                                </Grid>
                                                <div className={styles.check}>
                                                    <Botao size="sm" variant="outline-danger" icon={mdiTrashCan} title="Remover" onClick={() => removeDev(idx)} />
                                                </div>
                                            </HBox>
                                        );
                                    })}
                                </Card.Body>
                            </Card>
                        </>)}
                    </div>
                )}
            </VBox>
        </LayoutUsuario>
    );
}
